import React from 'react'
import { useAppSelector } from '../../../../../hooks/redux'

import ExpansionArrow from '../../../../atoms/ExpansionArrow'
import CustomFieldInput from '../../../CustomFieldInput'
import CustomFieldTable from '../../../CustomFieldTable'
import CustomFieldFlow from '@SonarCX/components/molecules/CustomFieldFlow'

import { chunkArray, isUUID } from '../../../../../utilities'
import { putUserMeta } from '../../../../../lib/api'
import { selectAccess } from '../../../../../slices/accessSlice'
import { useCustomerContext } from '../../../../../providers/CustomerProvider'
import { useMetaContext } from '../../../../../providers/MetaProvider'
import { useBreakpoint } from '../../../../../providers/BreakpointProvider'
import { selectCustomFields } from '../../../../../slices/customFields'
import { useAppDispatch } from '../../../../../hooks/redux'
import { AddErrorAlert, AddSuccessAlert } from '../../../../../slices/alert'
import useCustomFieldValues from '@SonarCX/hooks/useCustomFieldValues'
import useCustomFieldGroups from '../../../../../hooks/useCustomFieldGroups'
import useStyles from './styles'

type CustomerDetailsSectionProps = {
  user?: any
  readOnly?: boolean
  create?: boolean
  canEdit?: boolean
  modal?: boolean
  timelineMode?: boolean
  external?: boolean
  className?: string
}

const CustomerDetailsSection: React.FC<CustomerDetailsSectionProps> = ({
  user,
  readOnly,
  create,
  canEdit,
  modal,
  timelineMode = false,
  external,
  className
}) => {
  const {
    meta = [],
    metaCache,
    setMeta,
    fetchMeta,
    updateMetaVal
  } = useMetaContext()
  const { handleChange, handleSavedTags, setOld, setIsSaving, values } =
    useCustomerContext()
  const dispatch = useAppDispatch()
  const { isSmall } = useBreakpoint()
  const { loaded: accessLoaded } = useAppSelector(selectAccess)
  const { customFields } = useAppSelector(selectCustomFields)

  const { handleValueChange } = useCustomFieldValues({ meta, updateMetaVal })

  const [loaded, setLoaded] = React.useState(false)
  const [expanded, setExpanded] = React.useState(!timelineMode)
  const [fieldStatus, setFieldsStatus] = React.useState({
    filled: [],
    empty: []
  })
  const [metaRows, setMetaRows] = React.useState<APIMeta[]>([])
  const [shouldSave, setShouldSave] = React.useState(false)
  const tagRef = React.useRef([])
  const target = React.useMemo(
    () => (external ? 'external-user' : 'user'),
    [external]
  )

  const { groups, hasFields } = useCustomFieldGroups(
    target,
    user?.type_id ?? values.type
  )

  // Find fields for the current user type and sort by order ascending
  const allFields = React.useMemo(() => {
    // TODO: This may be replaced, when fields are not object specific
    const filtered = customFields.filter(field => field.object_type === target)
    return filtered
  }, [target, customFields])

  const classes = useStyles({
    readOnly,
    canEdit,
    smallMedia: isSmall,
    timelineMode
  })

  // load in meta from hook
  React.useEffect(() => {
    if (!user?.id || !accessLoaded) {
      return
    }
    if (!timelineMode && user.id in metaCache.current) {
      setMeta(metaCache.current[user.id])
    } else {
      setMeta([])
      fetchMeta(user.id, true, timelineMode, user?.name).catch(err =>
        dispatch(AddErrorAlert({ err }))
      )
    }
  }, [
    user,
    accessLoaded,
    setMeta,
    metaCache,
    fetchMeta,
    timelineMode,
    dispatch
  ])

  // load in meta from hook
  React.useEffect(() => {
    if (!meta) {
      return
    }

    if (!loaded && meta.length) {
      // sets meta for team page
      setOld(meta)

      meta.forEach(({ key, value, row, row_key }) => {
        handleChange(value, key, row, row_key)
      })

      const status = allFields.reduce(
        (accumulated, current) => {
          const hasValue = meta.find(data => data.key === current.id)
          if (hasValue) {
            accumulated.filled.push(current)
          } else {
            accumulated.empty.push(current)
          }

          return accumulated
        },
        { filled: [], empty: [] }
      )
      setFieldsStatus(status)
      setLoaded(true)
    }
  }, [meta, setOld, handleChange, loaded, allFields])

  // Saves meta after blur
  React.useEffect(() => {
    if (!shouldSave) {
      return
    }

    const id = user?.id
    if (!id) {
      return
    }

    setShouldSave(false)

    // Only save values with UUID keys and convert empty strings to null
    const saveableMeta = meta
      .filter(obj => isUUID(obj.key))
      .map(({ key, value, row, row_key }) => {
        let val

        if (value instanceof Date) {
          val = value.toISOString()
        } else {
          val = value.toString().trim()
          if (val.length === 0) {
            val = null
          }
        }
        if (row_key) {
          return { key, value: val, row, row_key }
        }

        return { key, value: val }
      })

    setIsSaving(true)
    // Save meta
    putUserMeta(id, saveableMeta)
      .then(meta => {
        dispatch(AddSuccessAlert('Changes saved', 500))
        setIsSaving(false)
        setMeta(meta)
      })
      .catch(err => {
        console.error('Error saving user meta', err)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldSave, meta, user, setIsSaving, dispatch])

  React.useEffect(() => {
    if (user?.tags?.length) {
      handleSavedTags(user.tags, 'tags')
      tagRef.current = user.tags
    }
  }, [user, handleSavedTags, modal])

  // Clean mock rows (meta rows) on data insert.
  React.useEffect(() => {
    if (metaRows.length === 0) {
      return
    }

    const tableIds = [...new Set(metaRows.map(mr => mr.key))]

    for (const tableId of tableIds) {
      const mockRows = metaRows.filter(x => x.key === tableId)
      const mockRowsRow = mockRows[0]?.row
      const tableOptions = customFields.find(field => field.id === tableId)

      if (
        meta
          .filter(
            x =>
              x.row === mockRowsRow &&
              x.key === tableId &&
              (tableOptions.options
                ? tableOptions.options.some(opt => opt === x.row_key)
                : (tableOptions.config?.options ?? []).some(
                    opt => opt.val === x.row_key
                  ))
          )
          .some(y => y.value !== '')
      ) {
        setMetaRows(old => {
          const cleanedRows = old.filter(o => o.key !== tableId)
          return cleanedRows
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta, metaRows])

  const handleBlur = React.useCallback(async () => {
    setShouldSave(true)
  }, [])

  if (readOnly && !create && !timelineMode) {
    return <div className={classes.noInfo}>No information found</div>
  }

  if (timelineMode) {
    return (
      <div className={classes.columnRoot}>
        {fieldStatus.filled.length === 0 ? (
          <div className={classes.noDataSection}>No data saved, yet</div>
        ) : (
          <div className={classes.column}>
            {fieldStatus.filled.map(field => (
              <CustomFieldInput
                key={field.id}
                field={field}
                value={meta.find(data => data.key === field.id)?.value ?? ''}
                disabled={readOnly}
                canEdit={canEdit}
                handleChange={handleValueChange}
                handleBlur={handleBlur}
                timelineMode={timelineMode}
                meta={meta}
                shouldFilter
              />
            ))}
          </div>
        )}
        {fieldStatus.empty.length > 0 && (
          <React.Fragment>
            <div
              className={classes.arrow}
              onClick={() => setExpanded(!expanded)}
            >
              <ExpansionArrow expanded={expanded} />{' '}
              {expanded ? 'SHOW LESS' : 'SHOW MORE'}
            </div>
            <div className={classes.column}>
              {expanded &&
                fieldStatus.empty.map(field => (
                  <CustomFieldInput
                    key={field.id}
                    field={field}
                    value={
                      meta.find(data => data.key === field.id)?.value ?? ''
                    }
                    disabled={readOnly}
                    canEdit={canEdit}
                    handleChange={handleValueChange}
                    handleBlur={handleBlur}
                    timelineMode={timelineMode}
                    meta={meta}
                    shouldFilter
                  />
                ))}
            </div>
          </React.Fragment>
        )}
      </div>
    )
  }

  if (!hasFields) {
    return null
  }

  const groupData = (
    <div className={classes.groupBorder}>
      {groups.map((group, i) => {
        const fields = group.field_ids
          .map(id => {
            const field = customFields.find(field => field.id === id)
            if (!field) {
              return null
            }

            if (['table', 'flow'].includes(field.field_type)) {
              return null
            }

            return (
              <div key={field.id} className={classes.gridItem}>
                <CustomFieldInput
                  field={field}
                  value={meta.find(data => data.key === field.id)?.value ?? ''}
                  disabled={readOnly}
                  canEdit={canEdit}
                  handleChange={handleValueChange}
                  handleBlur={handleBlur}
                  meta={meta}
                  shouldFilter
                />
              </div>
            )
          })
          .filter(Boolean)

        const tables = group.field_ids
          .map(id => {
            const field = customFields.find(field => field.id === id)
            if (!field) {
              return null
            }

            if (field.field_type !== 'table') {
              return null
            }

            return field
          })
          .filter(Boolean)

        const flows = group.field_ids
          .map(id => {
            const field = customFields.find(field => field.id === id)
            if (!field) {
              return null
            }

            if (field.field_type !== 'flow') {
              return null
            }

            return field
          })
          .filter(Boolean)

        const splitPoint = Math.ceil(fields.length / 2)
        const columns = chunkArray(fields, splitPoint)

        if (fields.length + tables.length + flows.length === 0) {
          return null
        }

        return (
          <React.Fragment key={`${group.id}-${i}`}>
            <div className={classes.root}>
              <div className={classes.title}>
                <span>{group.title.toLocaleUpperCase()}</span>
                {group.description && <span>{group.description}</span>}
              </div>

              {fields.length > 0 && (
                <div className={classes.gridReal}>
                  {columns.map((column, i) =>
                    column.map((item, j) => (
                      <div
                        key={`${i}-${j}`}
                        style={{
                          gridColumn: `${i + 1} / ${i + 2}`,
                          gridRow: `${j + 1} / ${j + 2}`
                        }}
                      >
                        {item}
                      </div>
                    ))
                  )}
                </div>
              )}

              {tables.length > 0 && (
                <div style={{ paddingBottom: 4, overflowY: 'auto' }}>
                  {tables.map(table => (
                    <CustomFieldTable
                      key={table.id}
                      table={table}
                      meta={meta}
                      setMeta={setMeta}
                      metaRows={metaRows}
                      setMetaRows={setMetaRows}
                      canEdit={canEdit}
                      handleValueChange={handleValueChange}
                      onBlur={handleBlur}
                    />
                  ))}
                </div>
              )}

              {flows.length > 0 && (
                <>
                  {flows.map(flow => {
                    return (
                      <CustomFieldFlow
                        meta={meta}
                        setMeta={setMeta}
                        key={flow.id}
                        flow={flow.config?.id}
                        canEdit={canEdit}
                        readOnly={readOnly}
                        objectType={target}
                        handleChange={handleValueChange}
                        handleBlur={handleBlur}
                      />
                    )
                  })}
                </>
              )}
            </div>
          </React.Fragment>
        )
      })}
    </div>
  )

  if (className) {
    return <div className={className}>{groupData}</div>
  }

  return groupData
}

export default CustomerDetailsSection
